import { Component, OnInit } from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { Router, ActivatedRoute } from "@angular/router";
import { finalize, forkJoin } from "rxjs";
import {
  Organization,
  BusinessUnit,
  AssetGroup,
  Plant,
} from "../../interfaces/asset-hierarchy/asset-hierarchy.models";
import { PlantHierarchy } from "../../interfaces/asset-hierarchy/plant-hierarchy.interface";
import {
  LkpCountry,
  LkpCvxCriticalityRanking,
} from "../../interfaces/turbines/lookup.interface";
import { Turbine } from "../../interfaces/turbines/turbine.interface";
import { AssetHierarchyService } from "../../services/asset-hierarchy.service";
import { TurbineService } from "../../services/turbine.service";
import { UserDto } from "../../interfaces/graph/user.interface";

@Component({
    selector: "app-turbine-edit",
    templateUrl: "./turbine-edit.component.html",
    styleUrls: ["./turbine-edit.component.css"],
    standalone: false
})
export class TurbineEditComponent implements OnInit {
  turbineForm: FormGroup;
  loading = true;
  loadingHierarchy = true;
  saving = false;
  error: string | null = null;
  validationErrors: string[] = [];
  turbineId: number;
  currentTurbine: Turbine | null = null;
  isInitialLoad = true; // Flag to prevent form subscriptions during initial load
  // Asset Hierarchy data
  organizations: Organization[] = [];
  businessUnits: BusinessUnit[] = [];
  assetGroups: AssetGroup[] = [];
  plants: Plant[] = [];

  // Lookup data from API
  countries: LkpCountry[] = [];
  cvxCriticalityRankings: LkpCvxCriticalityRanking[] = [];
  oemCriticalityRankingOptions: LkpCvxCriticalityRanking[] = [];

  gtOemOptions = [
    { value: "ALSTOM", label: "ALSTOM" },
    { value: "GE", label: "GE" },
    { value: "ROLLS ROYCE", label: "ROLLS ROYCE" },
    { value: "SIEMENS", label: "SIEMENS" },
    { value: "SOLAR", label: "SOLAR" },
  ];

  drivenEquipOptions = [
    { value: "Compressor", label: "Compressor" },
    { value: "Generator", label: "Generator" },
    { value: "Pump", label: "Pump" },
  ];

  fuelOptions = [
    { value: "Gas", label: "Gas" },
    { value: "Steam", label: "Steam" },
    { value: "Wind", label: "Wind" },
    { value: "Liquid", label: "Liquid" },
  ];

  cycleHzOptions = [
    { value: "50", label: "50Hz" },
    { value: "60", label: "60Hz" },
  ];

  boilTypeOptions = [
    { value: "NA", label: "NA" },
    { value: "EOR", label: "EOR" },
    { value: "Superheated", label: "Superheated" },
  ];

  installationTypeOptions = [
    { value: "MECHANICAL DRIVE", label: "MECHANICAL DRIVE" },
    { value: "POWER GENERATION", label: "POWER GENERATION" },
    { value: "NOT YET RUNNING", label: "NOT YET RUNNING" },
    { value: "OUT OF SERVICE", label: "OUT OF SERVICE" },
    { value: "UNKNOWN", label: "UNKNOWN" },
    { value: "SPARE", label: "SPARE" },
  ];

  serviceTypeOptions = [
    { value: "Heat_Recovery", label: "Heat Recovery" },
    { value: "Simple_Cycle", label: "Simple Cycle" },
  ];

  oemSupportOptions = [{ value: "LTSA", label: "LTSA" }];
  oemSupportRelationshipOptions = [
    { value: "LTSA", label: "LTSA" },
    { value: "Service_Agreement", label: "Service Agreement" },
    { value: "Parts_Only", label: "Parts Only" },
    { value: "None", label: "None" },
  ];

  constructor(
    private fb: FormBuilder,
    private turbineService: TurbineService,
    private assetHierarchyService: AssetHierarchyService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.turbineForm = this.createForm();
  }
  ngOnInit(): void {
    this.turbineId = +this.route.snapshot.paramMap.get("id")!;

    // Load all data in parallel for maximum performance
    this.loadAllDataInParallel();
  }
  private createForm(): FormGroup {
    return this.fb.group({
      // Asset Hierarchy - Only plantId is stored in DB, others are for UI navigation
      organizationId: ["", Validators.required],
      businessUnitId: [{ value: "", disabled: true }, Validators.required],
      assetGroupId: [{ value: "", disabled: true }, Validators.required],
      plantId: [{ value: "", disabled: true }, Validators.required],

      // Turbine Details
      serialNumber: ["", Validators.required],
      unitShortName: [""],
      chevronIdentifier: [""],
      gtOem: [""],
      drivenEquip: [""],
      drivenEquipOem: [""],
      frameType: [""],
      equipCode: [""],
      cvxCriticalityRanking: [""],
      oemCriticalityRanking: [""],
      installYear: ["", [Validators.min(1900), Validators.max(2050)]],
      firstFireDate: [""],
      capacity: [""],
      rpm: [""],
      cycleHz: [""],
      primaryFuel: [""],
      altFuel1: [""],
      altFuel2: [""],
      gtControlSystem: [""],
      excitationSystem: [""],
      combustionSystem: [""],
      boilType: [""],
      installationType: [""],
      serviceHours: [""],
      latitude: [""],
      longitude: [""],
      country: [""],
      serviceType: [""],
      offshore: [""],
      statusDescription: [""],
      turbineStatus: [""],
      nextPlannedOutage: [""],
      lastPlannedOutage: [""],
      baseloadFiringTemperature: [""],
      designHeatRate: [""],
      oemSiteAccountManager: [""],
      oemSiteAccountManagerEmail: ["", Validators.email],
      oemCorporateAccountManager: [""],
      oemCorporateAccountManagerEmail: ["", Validators.email],
      cvxCorporateContact: [""],
      cvxCorporateContactEmail: ["", Validators.email],
      cvxSiteContact: [""],
      cvxSiteContactEmail: ["", Validators.email],
      oemSupportRelationship: [""],
      endServiceDate: [""],

      // RAM Targets
      availabilityPercentTargetGreen: [
        "",
        [Validators.min(0), Validators.max(100)],
      ],
      availabilityPercentTargetYellow: [
        "",
        [Validators.min(0), Validators.max(100)],
      ],
      reliabilityPercentTargetGreen: [
        "",
        [Validators.min(0), Validators.max(100)],
      ],
      reliabilityPercentTargetYellow: [
        "",
        [Validators.min(0), Validators.max(100)],
      ],

      // Monitoring Controls
      pscMscMonitored: [false],
      localDscMonitored: [false],
      impactManaged: [false],
      orapReported: [false],
      oemMonitored: [false],

      // Equipment ID fields
      cuprEquipmentId: [""],
      eamMmsEquipmentId: [""],
      topsId: [""],
    });
  }
  private loadTurbine(): void {
    this.loading = true;
    this.error = null;

    this.turbineService.getTurbine(this.turbineId).subscribe({
      next: (turbine) => {
        this.currentTurbine = turbine;
        this.populateForm(turbine);
        this.loading = false; // Set loading to false after form is populated
      },
      error: (error) => {
        this.loading = false; // Set loading to false on error
        this.error = "Failed to load turbine. Please try again.";
        console.error("Error loading turbine:", error);
      },
    });
  }

  /**
   * Optimized loading method that loads all required data in parallel using forkJoin
   */
  private loadAllDataInParallel(): void {
    this.loading = true;
    this.loadingHierarchy = true;
    this.error = null;

    // Setup form subscriptions first
    this.setupFormSubscriptions();

    // Load all data in parallel using forkJoin
    forkJoin({
      turbine: this.turbineService.getTurbine(this.turbineId),
      organizations: this.assetHierarchyService.getOrganizations(),
      countries: this.turbineService.getCountries(),
      cvxCriticalityRankings: this.turbineService.getCvxCriticalityRankings(),
    }).subscribe({
      next: ({ turbine, organizations, countries, cvxCriticalityRankings }) => {
        // Store data
        this.currentTurbine = turbine;
        this.organizations = organizations;
        this.countries = countries;
        this.cvxCriticalityRankings = cvxCriticalityRankings;
        this.oemCriticalityRankingOptions = cvxCriticalityRankings;

        // Load hierarchy data efficiently if plantId exists
        if (turbine.plantId) {
          this.loadHierarchyOptimized(turbine);
        } else {
          // If no plant, just populate the basic form data
          this.populateFormBasicData(turbine);
          this.loading = false;
          this.loadingHierarchy = false;
          this.isInitialLoad = false;
        }
      },
      error: (error) => {
        console.error("Error loading data:", error);
        this.error = "Failed to load form data. Please try again.";
        this.loading = false;
        this.loadingHierarchy = false;
        this.isInitialLoad = false;
      },
    });
  }

  /**
   * Optimized hierarchy loading using the new plant hierarchy API
   */
  private loadHierarchyOptimized(turbine: Turbine): void {
    if (!turbine.plantId) {
      this.populateFormBasicData(turbine);
      this.loading = false;
      this.loadingHierarchy = false;
      this.isInitialLoad = false;
      return;
    }

    // Use the new optimized API to get the complete hierarchy in one call
    this.assetHierarchyService.getPlantHierarchy(turbine.plantId).subscribe({
      next: (hierarchy) => {
        if (hierarchy) {
          // Load the dependent dropdowns in parallel
          forkJoin({
            businessUnits:
              this.assetHierarchyService.getBusinessUnitsByOrganizationId(
                hierarchy.organizationId
              ),
            assetGroups:
              this.assetHierarchyService.getAssetGroupsByBusinessUnitId(
                hierarchy.businessUnitId
              ),
            plants: this.assetHierarchyService.getPlantsByAssetGroupId(
              hierarchy.assetGroupId
            ),
          }).subscribe({
            next: ({ businessUnits, assetGroups, plants }) => {
              // Store the loaded data
              this.businessUnits = businessUnits;
              this.assetGroups = assetGroups;
              this.plants = plants;

              // Populate the complete form including hierarchy
              this.populateFormWithHierarchy(turbine, hierarchy);

              this.loading = false;
              this.loadingHierarchy = false;
              this.isInitialLoad = false;
            },
            error: (error) => {
              console.error("Error loading hierarchy dropdowns:", error);
              this.error = "Failed to load hierarchy data. Please try again.";
              this.loading = false;
              this.loadingHierarchy = false;
              this.isInitialLoad = false;
            },
          });
        } else {
          // Fallback to basic form population if hierarchy not found
          this.populateFormBasicData(turbine);
          this.loading = false;
          this.loadingHierarchy = false;
          this.isInitialLoad = false;
        }
      },
      error: (error) => {
        console.error("Error loading plant hierarchy:", error);
        // Fallback to basic form population
        this.populateFormBasicData(turbine);
        this.loading = false;
        this.loadingHierarchy = false;
        this.isInitialLoad = false;
      },
    });
  }

  /**
   * Populate form with basic turbine data (non-hierarchy fields)
   */
  private populateFormBasicData(turbine: Turbine): void {
    const formatDate = (dateString: string | undefined): string => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
    };

    this.turbineForm.patchValue({
      serialNumber: turbine.serialNumber || "",
      unitShortName: turbine.unitShortName || "",
      chevronIdentifier: turbine.chevronIdentifier || "",
      gtOem: turbine.gtOem || "",
      drivenEquip: turbine.drivenEquip || "",
      drivenEquipOem: turbine.drivenEquipOem || "",
      frameType: turbine.frameType || "",
      equipCode: turbine.equipCode || "",
      cvxCriticalityRanking: turbine.cvxCriticalityRanking || "",
      oemCriticalityRanking: turbine.oemCriticalityRanking || "",
      installYear: turbine.installYear || "",
      firstFireDate: formatDate(turbine.firstFireDate),
      capacity: turbine.capacity || "",
      rpm: turbine.rpm || "",
      cycleHz: turbine.cycleHz || "",
      primaryFuel: turbine.primaryFuel || "",
      altFuel1: turbine.altFuel1 || "",
      altFuel2: turbine.altFuel2 || "",
      gtControlSystem: turbine.gtControlSystem || "",
      excitationSystem: turbine.excitationSystem || "",
      combustionSystem: turbine.combustionSystem || "",
      boilType: turbine.boilType || "",
      installationType: turbine.installationType || "",
      serviceHours: turbine.serviceHours || "",
      latitude: turbine.latitude || "",
      longitude: turbine.longitude || "",
      country: turbine.country || "",
      serviceType: turbine.serviceType || "",
      nextPlannedOutage: formatDate(turbine.nextPlannedOutage),
      lastPlannedOutage: formatDate(turbine.lastPlannedOutage),
      baseloadFiringTemperature: turbine.baseloadFiringTemperature || "",
      designHeatRate: turbine.designHeatrate || "",
      oemSiteAccountManager: turbine.oemSiteAccountManager || "",
      oemSiteAccountManagerEmail: turbine.oemSiteAccountManagerEmail || "",
      oemCorporateAccountManager: turbine.oemCorporateAccountManager || "",
      oemCorporateAccountManagerEmail:
        turbine.oemCorporateAccountManagerEmail || "",
      cvxCorporateContact: turbine.cvxCorporateContact || "",
      cvxCorporateContactEmail: turbine.cvxCorporateContactEmail || "",
      cvxSiteContact: turbine.cvxSiteContact || "",
      cvxSiteContactEmail: turbine.cvxSiteContactEmail || "",
      oemSupportRelationship: turbine.oemSupportRelationship || "",
      endServiceDate: formatDate(turbine.endServiceDate),
      availabilityPercentTargetGreen:
        turbine.availabilityPercentTargetGreen || "",
      availabilityPercentTargetYellow:
        turbine.availabilityPercentTargetYellow || "",
      reliabilityPercentTargetGreen:
        turbine.reliabilityPercentTargetGreen || "",
      reliabilityPercentTargetYellow:
        turbine.reliabilityPercentTargetYellow || "",
      pscMscMonitored: turbine.pscMscMonitored || false,
      localDscMonitored: turbine.localdscMonitored || false,
      impactManaged: turbine.impactManaged || false,
      orapReported: turbine.orapReported || false,
      oemMonitored: turbine.oemMonitored || false,
      cuprEquipmentId: turbine.cuprEquipmentId || "",
      eamMmsEquipmentId: turbine.eamMmsEquipmentId || "",
      topsId: turbine.topsId || "",
    });
  }

  /**
   * Populate form with both basic data and hierarchy information
   */
  private populateFormWithHierarchy(
    turbine: Turbine,
    hierarchy: PlantHierarchy
  ): void {
    // First populate basic data
    this.populateFormBasicData(turbine);

    // Then populate hierarchy fields and enable controls
    this.turbineForm.patchValue({
      organizationId: hierarchy.organizationId,
      businessUnitId: hierarchy.businessUnitId,
      assetGroupId: hierarchy.assetGroupId,
      plantId: hierarchy.plantId,
    });

    // Enable hierarchy controls
    this.turbineForm.get("businessUnitId")?.enable();
    this.turbineForm.get("assetGroupId")?.enable();
    this.turbineForm.get("plantId")?.enable();
  }
  private populateForm(turbine: Turbine): void {
    // Format dates to YYYY-MM-DD for date inputs
    const formatDate = (dateString: string | undefined): string => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
    };

    // First populate non-hierarchy fields
    this.turbineForm.patchValue({
      // Turbine Details
      serialNumber: turbine.serialNumber || "",
      unitShortName: turbine.unitShortName || "",
      chevronIdentifier: turbine.chevronIdentifier || "",
      gtOem: turbine.gtOem || "",
      drivenEquip: turbine.drivenEquip || "",
      drivenEquipOEM: turbine.drivenEquipOEM || "",
      frameType: turbine.frameType || "",
      equipCode: turbine.equipCode || "",
      cvxCriticalityRanking: turbine.cvxCriticalityRanking || "",
      oemCriticalityRanking: turbine.oemCriticalityRanking || "",
      installYear: turbine.installYear || "",
      firstFireDate: formatDate(turbine.firstFireDate),
      capacity: turbine.capacity || "",
      rpm: turbine.rpm || "",
      cycleHz: turbine.cycleHz || "",
      primaryFuel: turbine.primaryFuel || "",
      altFuel1: turbine.altFuel1 || "",
      altFuel2: turbine.altFuel2 || "",
      gtControlSystem: turbine.gtControlSystem || "",
      excitationSystem: turbine.excitationSystem || "",
      combustionSystem: turbine.combustionSystem || "",
      boilType: turbine.boilType || "",
      installationType: turbine.installationType || "",
      serviceHours: turbine.serviceHours || "",
      latitude: turbine.latitude || "",
      longitude: turbine.longitude || "",
      country: turbine.country || "",
      serviceType: turbine.serviceType || "",
      nextPlannedOutage: formatDate(turbine.nextPlannedOutage),
      lastPlannedOutage: formatDate(turbine.lastPlannedOutage),
      baseloadFiringTemperature: turbine.baseloadFiringTemperature || "",
      designHeatRate: turbine.designHeatrate || "",
      oemSiteAccountManager: turbine.oemSiteAccountManager || "",
      oemSiteAccountManagerEmail: turbine.oemSiteAccountManagerEmail || "",
      oemCorporateAccountManager: turbine.oemCorporateAccountManager || "",
      oemCorporateAccountManagerEmail:
        turbine.oemCorporateAccountManagerEmail || "",
      cvxCorporateContact: turbine.cvxCorporateContact || "",
      cvxCorporateContactEmail: turbine.cvxCorporateContactEmail || "",
      cvxSiteContact: turbine.cvxSiteContact || "",
      cvxSiteContactEmail: turbine.cvxSiteContactEmail || "",
      oemSupportRelationship: turbine.oemSupportRelationship || "",
      endServiceDate: formatDate(turbine.endServiceDate),

      // RAM Targets
      availabilityPercentTargetGreen:
        turbine.availabilityPercentTargetGreen || "",
      availabilityPercentTargetYellow:
        turbine.availabilityPercentTargetYellow || "",
      reliabilityPercentTargetGreen:
        turbine.reliabilityPercentTargetGreen || "",
      reliabilityPercentTargetYellow:
        turbine.reliabilityPercentTargetYellow || "",

      // Monitoring Controls
      pscMscMonitored: turbine.pscMscMonitored || false,
      localDscMonitored: turbine.localdscMonitored || false,
      impactManaged: turbine.impactManaged || false,
      orapReported: turbine.orapReported || false,
      oemMonitored: turbine.oemMonitored || false,
    });

    // Load the cascade dropdowns and populate hierarchy fields in order
    this.loadHierarchyAndPopulate(turbine);
  }
  private async loadHierarchyAndPopulate(turbine: Turbine): Promise<void> {
    const plantId = turbine.plantId;

    try {
      if (plantId) {
        // Find the plant and work backwards through the hierarchy
        const hierarchyData = await this.findPlantHierarchy(plantId);

        if (hierarchyData) {
          // Load all dependent data in the correct order
          await this.loadBusinessUnitsAsync(hierarchyData.organizationId);
          await this.loadAssetGroupsAsync(hierarchyData.businessUnitId);
          await this.loadPlantsAsync(hierarchyData.assetGroupId);

          // Enable controls as data becomes available
          this.turbineForm.get("businessUnitId")?.enable();
          this.turbineForm.get("assetGroupId")?.enable();
          this.turbineForm.get("plantId")?.enable();

          // Set form values in the correct order
          this.turbineForm.patchValue({
            organizationId: hierarchyData.organizationId,
            businessUnitId: hierarchyData.businessUnitId,
            assetGroupId: hierarchyData.assetGroupId,
            plantId: plantId,
          });
        }
      }

      // Mark initial load as complete
      this.isInitialLoad = false;
      this.loadingHierarchy = false; // Set loadingHierarchy to false after hierarchy is loaded
    } catch (error) {
      console.error("Error loading hierarchy data:", error);
      this.error = "Failed to load asset hierarchy data. Please try again.";
      this.isInitialLoad = false;
    }
  }

  private async findPlantHierarchy(plantId: number): Promise<{
    organizationId: number;
    businessUnitId: number;
    assetGroupId: number;
  } | null> {
    try {
      // First, we need to find which asset group contains this plant
      // We'll search through all organizations, business units, and asset groups
      for (const org of this.organizations) {
        const businessUnits = await this.getBusinessUnitsAsync(org.id);

        for (const bu of businessUnits) {
          const assetGroups = await this.getAssetGroupsAsync(bu.id);

          for (const ag of assetGroups) {
            const plants = await this.getPlantsAsync(ag.id);

            if (plants.some((p) => p.id === plantId)) {
              return {
                organizationId: org.id,
                businessUnitId: bu.id,
                assetGroupId: ag.id,
              };
            }
          }
        }
      }

      return null;
    } catch (error) {
      console.error("Error finding plant hierarchy:", error);
      return null;
    }
  }

  private async getBusinessUnitsAsync(
    organizationId: number
  ): Promise<BusinessUnit[]> {
    return new Promise((resolve, reject) => {
      this.assetHierarchyService
        .getBusinessUnitsByOrganizationId(organizationId)
        .subscribe({
          next: (businessUnits) => resolve(businessUnits),
          error: (error) => reject(error),
        });
    });
  }

  private async getAssetGroupsAsync(
    businessUnitId: number
  ): Promise<AssetGroup[]> {
    return new Promise((resolve, reject) => {
      this.assetHierarchyService
        .getAssetGroupsByBusinessUnitId(businessUnitId)
        .subscribe({
          next: (assetGroups) => resolve(assetGroups),
          error: (error) => reject(error),
        });
    });
  }

  private async getPlantsAsync(assetGroupId: number): Promise<Plant[]> {
    return new Promise((resolve, reject) => {
      this.assetHierarchyService
        .getPlantsByAssetGroupId(assetGroupId)
        .subscribe({
          next: (plants) => resolve(plants),
          error: (error) => reject(error),
        });
    });
  }
  private loadBusinessUnitsAsync(organizationId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.assetHierarchyService
        .getBusinessUnitsByOrganizationId(organizationId)
        .subscribe({
          next: (businessUnits) => {
            this.businessUnits = businessUnits;
            // Enable business unit dropdown if data is available
            if (businessUnits.length > 0) {
              this.turbineForm.get("businessUnitId")?.enable();
            }
            resolve();
          },
          error: (error) => {
            reject(error);
          },
        });
    });
  }

  private loadAssetGroupsAsync(businessUnitId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.assetHierarchyService
        .getAssetGroupsByBusinessUnitId(businessUnitId)
        .subscribe({
          next: (assetGroups) => {
            this.assetGroups = assetGroups;
            // Enable asset group dropdown if data is available
            if (assetGroups.length > 0) {
              this.turbineForm.get("assetGroupId")?.enable();
            }
            resolve();
          },
          error: (error) => {
            reject(error);
          },
        });
    });
  }

  private loadPlantsAsync(assetGroupId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.assetHierarchyService
        .getPlantsByAssetGroupId(assetGroupId)
        .subscribe({
          next: (plants) => {
            this.plants = plants;
            // Enable plant dropdown if data is available
            if (plants.length > 0) {
              this.turbineForm.get("plantId")?.enable();
            }
            resolve();
          },
          error: (error) => {
            reject(error);
          },
        });
    });
  }
  private setupFormSubscriptions(): void {
    // Organization change - clear dependent dropdowns
    this.turbineForm.get("organizationId")?.valueChanges.subscribe((orgId) => {
      if (orgId && !this.isInitialLoad) {
        // Only trigger during user interactions, not initial load
        this.loadBusinessUnits(orgId);
        this.turbineForm.patchValue({
          businessUnitId: "",
          assetGroupId: "",
          plantId: "",
        });
        this.businessUnits = [];
        this.assetGroups = [];
        this.plants = [];

        // Enable business unit dropdown
        this.turbineForm.get("businessUnitId")?.enable();
        // Disable dependent dropdowns
        this.turbineForm.get("assetGroupId")?.disable();
        this.turbineForm.get("plantId")?.disable();
      }
    });

    // Business Unit change - clear dependent dropdowns
    this.turbineForm.get("businessUnitId")?.valueChanges.subscribe((buId) => {
      if (buId && !this.isInitialLoad) {
        // Only trigger during user interactions, not initial load
        this.loadAssetGroups(buId);
        this.turbineForm.patchValue({
          assetGroupId: "",
          plantId: "",
        });
        this.assetGroups = [];
        this.plants = [];

        // Enable asset group dropdown
        this.turbineForm.get("assetGroupId")?.enable();
        // Disable dependent dropdown
        this.turbineForm.get("plantId")?.disable();
      }
    });

    // Asset Group change - clear dependent dropdowns
    this.turbineForm.get("assetGroupId")?.valueChanges.subscribe((agId) => {
      if (agId && !this.isInitialLoad) {
        // Only trigger during user interactions, not initial load
        this.loadPlants(agId);
        this.turbineForm.patchValue({
          plantId: "",
        });
        this.plants = [];

        // Enable plant dropdown
        this.turbineForm.get("plantId")?.enable();
      }
    });
  }
  private loadOrganizations(): void {
    this.assetHierarchyService.getOrganizations().subscribe({
      next: (orgs) => {
        this.organizations = orgs;
      },
      error: (error) => {
        this.error = "Failed to load organizations. Please try again.";
        console.error("Error loading organizations:", error);
      },
    });
  }

  private async loadOrganizationsAsync(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.assetHierarchyService.getOrganizations().subscribe({
        next: (orgs) => {
          this.organizations = orgs;
          resolve();
        },
        error: (error) => {
          console.error("Error loading organizations:", error);
          reject(error);
        },
      });
    });
  }

  private loadBusinessUnits(organizationId: number): void {
    this.assetHierarchyService
      .getBusinessUnitsByOrganizationId(organizationId)
      .subscribe({
        next: (businessUnits) => {
          this.businessUnits = businessUnits;
        },
        error: (error) => {
          this.error = "Failed to load business units. Please try again.";
          console.error("Error loading business units:", error);
        },
      });
  }

  private loadAssetGroups(businessUnitId: number): void {
    this.assetHierarchyService
      .getAssetGroupsByBusinessUnitId(businessUnitId)
      .subscribe({
        next: (assetGroups) => {
          this.assetGroups = assetGroups;
        },
        error: (error) => {
          this.error = "Failed to load asset groups. Please try again.";
          console.error("Error loading asset groups:", error);
        },
      });
  }
  private loadPlants(assetGroupId: number): void {
    this.assetHierarchyService.getPlantsByAssetGroupId(assetGroupId).subscribe({
      next: (plants) => {
        this.plants = plants;
      },
      error: (error) => {
        this.error = "Failed to load plants. Please try again.";
        console.error("Error loading plants:", error);
      },
    });
  }

  private loadLookupData(): void {
    // Load countries
    this.turbineService.getCountries().subscribe({
      next: (countries) => {
        this.countries = countries;
      },
      error: (error) => {
        console.error("Error loading countries:", error);
        // Don't set error message here as it's not critical for edit functionality
      },
    });

    // Load CVX criticality rankings
    this.turbineService.getCvxCriticalityRankings().subscribe({
      next: (rankings) => {
        this.cvxCriticalityRankings = rankings;
        this.oemCriticalityRankingOptions = rankings;
      },
      error: (error) => {
        console.error("Error loading CVX criticality rankings:", error);
        // Don't set error message here as it's not critical for edit functionality
      },
    });
  }

  private formatDateForApi(dateValue: any): string | undefined {
    if (!dateValue) {
      return undefined;
    }

    // If it's already a string in YYYY-MM-DD format, return as is
    if (
      typeof dateValue === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(dateValue)
    ) {
      return dateValue;
    }

    // Convert Date object or other format to YYYY-MM-DD
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) {
      return undefined;
    }

    return date.toISOString().split("T")[0];
  }
  onSubmit(): void {
    // Clear previous validation errors
    this.validationErrors = [];
    this.error = null;

    // Check form validity and collect basic validation errors
    if (!this.turbineForm.valid) {
      this.validationErrors = this.getBasicValidationErrors();
      this.markFormGroupTouched();
      return;
    }

    this.saving = true;
    const formValue = this.turbineForm.value;

      // Remove hierarchy IDs from form value - only keep plantId
      const { organizationId, businessUnitId, assetGroupId } = formValue; // Properly format the turbine data with correct type conversions
      // Don't spread currentTurbine to avoid EF tracking conflicts
      const turbineData: Turbine = {
        id: this.turbineId, // Only include the ID for identification
        plantId: formValue.plantId,
        country: formValue.country,
        equipCode: formValue.equipCode,
        serialNumber: formValue.serialNumber,
        unitShortName: formValue.unitShortName,
        chevronIdentifier: formValue.chevronIdentifier,
        gtOem: formValue.gtOem,
        drivenEquip: formValue.drivenEquip,
        drivenEquipOem: formValue.drivenEquipOem,
        frameType: formValue.frameType,
        cvxCriticalityRanking: formValue.cvxCriticalityRanking,
        oemCriticalityRanking: formValue.oemCriticalityRanking,
        boilType: formValue.boilType,
        installationType: formValue.installationType,
        serviceType: formValue.serviceType,
        latitude: formValue.latitude,
        longitude: formValue.longitude,
        gtControlSystem: formValue.gtControlSystem,
        excitationSystem: formValue.excitationSystem,
        combustionSystem: formValue.combustionSystem,
        oemSupportRelationship: formValue.oemSupportRelationship,
        oemSiteAccountManager: formValue.oemSiteAccountManager,
        oemSiteAccountManagerEmail: formValue.oemSiteAccountManagerEmail,
        oemCorporateAccountManager: formValue.oemCorporateAccountManager,
        oemCorporateAccountManagerEmail:
          formValue.oemCorporateAccountManagerEmail,
        cvxCorporateContact: formValue.cvxCorporateContact,
        cvxCorporateContactEmail: formValue.cvxCorporateContactEmail,
        cvxSiteContact: formValue.cvxSiteContact,
        cvxSiteContactEmail: formValue.cvxSiteContactEmail,
        pscMscMonitored: formValue.pscMscMonitored,
        oemMonitored: formValue.oemMonitored,
        localdscMonitored: formValue.localDscMonitored,
        impactManaged: formValue.impactManaged,
        orapReported: formValue.orapReported,

        // Equipment ID fields
        cuprEquipmentId: formValue.cuprEquipmentId || undefined,
        eamMmsEquipmentId: formValue.eamMmsEquipmentId || undefined,
        topsId: formValue.topsId || undefined,

        // Ensure numeric fields are properly converted (empty strings become undefined)
        capacity: formValue.capacity ? Number(formValue.capacity) : undefined,
        rpm: formValue.rpm ? Number(formValue.rpm) : undefined,
        cycleHz: formValue.cycleHz ? Number(formValue.cycleHz) : undefined,
        serviceHours: formValue.serviceHours
          ? Number(formValue.serviceHours)
          : undefined,
        baseloadFiringTemperature: formValue.baseloadFiringTemperature
          ? Number(formValue.baseloadFiringTemperature)
          : undefined,
        installYear: formValue.installYear
          ? Number(formValue.installYear)
          : undefined,
        // Ensure string fields with || undefined for optional fields
        primaryFuel: formValue.primaryFuel || undefined,
        altFuel1: formValue.altFuel1 || undefined,
        altFuel2: formValue.altFuel2 || undefined,
        designHeatrate: formValue.designHeatRate || undefined, // Note: form field name difference
        offshore: formValue.offshore || undefined,
        statusDescription: formValue.statusDescription || undefined,
        turbineStatus: formValue.turbineStatus || undefined,
        // Ensure percentage fields are properly converted
        availabilityPercentTargetGreen: formValue.availabilityPercentTargetGreen
          ? Number(formValue.availabilityPercentTargetGreen)
          : undefined,
        availabilityPercentTargetYellow:
          formValue.availabilityPercentTargetYellow
            ? Number(formValue.availabilityPercentTargetYellow)
            : undefined,
        reliabilityPercentTargetGreen: formValue.reliabilityPercentTargetGreen
          ? Number(formValue.reliabilityPercentTargetGreen)
          : undefined,
        reliabilityPercentTargetYellow: formValue.reliabilityPercentTargetYellow
          ? Number(formValue.reliabilityPercentTargetYellow)
          : undefined,
        // Format dates for API
        firstFireDate: this.formatDateForApi(formValue.firstFireDate),
        nextPlannedOutage: this.formatDateForApi(formValue.nextPlannedOutage),
        lastPlannedOutage: this.formatDateForApi(formValue.lastPlannedOutage),
        endServiceDate: this.formatDateForApi(formValue.endServiceDate),
      };

      this.turbineService
        .updateTurbine(this.turbineId, turbineData)
        .pipe(
          finalize(() => {
            this.saving = false;
          })
        )
        .subscribe({
          next: (updatedTurbine) => {
            this.router.navigate(["/turbines/edit", updatedTurbine.id]);
          },
          error: (error) => {
            this.error = "Failed to update turbine. Please try again.";
            console.error("Error updating turbine:", error);
          },
        });
  }

  onCancel(): void {
    this.router.navigate(["/turbines"]);
  }

  private markFormGroupTouched(): void {
    Object.keys(this.turbineForm.controls).forEach((key) => {
      const control = this.turbineForm.get(key);
      if (control) {
        control.markAsTouched();
      }
    });
  }

  private getBasicValidationErrors(): string[] {
    const errors: string[] = [];
    
    if (this.turbineForm.get('organizationId')?.invalid) {
      errors.push('Organization is required');
    }
    if (this.turbineForm.get('businessUnitId')?.invalid) {
      errors.push('Business Unit is required');
    }
    if (this.turbineForm.get('assetGroupId')?.invalid) {
      errors.push('Asset Group is required');
    }
    if (this.turbineForm.get('plantId')?.invalid) {
      errors.push('Plant is required');
    }
    if (this.turbineForm.get('serialNumber')?.invalid) {
      errors.push('Serial Number is required');
    }
    
    const emailFields = ['oemSiteAccountManagerEmail', 'oemCorporateAccountManagerEmail', 'cvxCorporateContactEmail', 'cvxSiteContactEmail'];
    emailFields.forEach(field => {
      const control = this.turbineForm.get(field);
      if (control?.invalid && control?.errors?.['email']) {
        errors.push(`${field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} must be a valid email address`);
      }
    });
    
    return errors;
  }

  // Helper methods for validation
  isFieldInvalid(fieldName: string): boolean {
    const field = this.turbineForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.turbineForm.get(fieldName);
    if (field && field.errors) {
      if (field.errors["required"]) {
        return `${fieldName} is required`;
      }
      if (field.errors["email"]) {
        return "Please enter a valid email address";
      }
      if (field.errors["min"]) {
        return `Value must be at least ${field.errors["min"].min}`;
      }
      if (field.errors["max"]) {
        return `Value must be at most ${field.errors["max"].max}`;
      }
    }
    return "";
  }

  onCvxCorporateContactSelected(user: UserDto | null): void {
    if (user) {
      this.turbineForm.patchValue({
        cvxCorporateContact: `${user.firstName} ${user.lastName}`,
        cvxCorporateContactEmail: user.email
      });
    } else {
      this.turbineForm.patchValue({
        cvxCorporateContact: '',
        cvxCorporateContactEmail: ''
      });
    }
  }

  onCvxCorporateContactEmailSelected(user: UserDto | null): void {
    if (user) {
      this.turbineForm.patchValue({
        cvxCorporateContact: `${user.firstName} ${user.lastName}`,
        cvxCorporateContactEmail: user.email
      });
    } else {
      this.turbineForm.patchValue({
        cvxCorporateContact: '',
        cvxCorporateContactEmail: ''
      });
    }
  }

  onCvxSiteContactSelected(user: UserDto | null): void {
    if (user) {
      this.turbineForm.patchValue({
        cvxSiteContact: `${user.firstName} ${user.lastName}`,
        cvxSiteContactEmail: user.email
      });
    } else {
      this.turbineForm.patchValue({
        cvxSiteContact: '',
        cvxSiteContactEmail: ''
      });
    }
  }

  onCvxSiteContactEmailSelected(user: UserDto | null): void {
    if (user) {
      this.turbineForm.patchValue({
        cvxSiteContact: `${user.firstName} ${user.lastName}`,
        cvxSiteContactEmail: user.email
      });
    } else {
      this.turbineForm.patchValue({
        cvxSiteContact: '',
        cvxSiteContactEmail: ''
      });
    }
  }
}
